import type { LaunchBillingPlanId, PlanChangePreview, PlanRoleLimitKey, UsageBillingSnapshot } from '@kangaroo/shared';

import { api } from '../lib/api';

interface ApiResponse<T> {
  data: T;
}

export async function previewPlanChange(planId: LaunchBillingPlanId): Promise<PlanChangePreview> {
  const response = await api.post<ApiResponse<PlanChangePreview>>('/billing/teams/current/preview', { planId });
  return response.data.data;
}

export type BillingCheckoutResult =
  | {
      type: 'checkout';
      url: string;
      teamId?: string;
      workspaceConverted?: boolean;
      reactivatedClubTeam?: boolean;
    }
  | {
      type: 'updated';
      planId: string;
      teamId?: string;
      workspaceConverted?: boolean;
      reactivatedClubTeam?: boolean;
    };

export async function startBillingCheckout(
  planId: LaunchBillingPlanId,
  retention?: Partial<Record<PlanRoleLimitKey, string[]>>,
): Promise<BillingCheckoutResult> {
  const response = await api.post<ApiResponse<BillingCheckoutResult>>('/billing/teams/current/checkout', {
    planId,
    retention,
  });
  return response.data.data;
}

export async function openBillingPortal(): Promise<{ url: string }> {
  const response = await api.post<ApiResponse<{ url: string }>>('/billing/teams/current/portal');
  return response.data.data;
}

export async function openBillingCancellationFlow(): Promise<{ url: string }> {
  const response = await api.post<ApiResponse<{ url: string }>>('/billing/teams/current/cancel');
  return response.data.data;
}

export async function redeemPartnerCode(code: string): Promise<{ partnerCode: { code: string; label: string } }> {
  const response = await api.post<ApiResponse<{ partnerCode: { code: string; label: string } }>>(
    '/billing/teams/current/partner-code',
    { code },
  );
  return response.data.data;
}

export async function syncTeamBilling(checkoutSessionId?: string | null): Promise<void> {
  await api.post('/billing/teams/current/sync', checkoutSessionId ? { checkoutSessionId } : {});
}

export const currentUsageBillingKey = '/billing/usage/current';

export async function fetchUsageBilling(): Promise<UsageBillingSnapshot> {
  const response = await api.get<ApiResponse<UsageBillingSnapshot>>(currentUsageBillingKey);
  return response.data.data;
}

export type BillingAccountSelection = 'current' | 'personal';
export type CustomerBillingSnapshot = UsageBillingSnapshot & { usageActionsAvailable: boolean; personalAccountAvailable: boolean };
export const currentCustomerBillingKey = '/billing/current';
export async function fetchCustomerBilling(selection: BillingAccountSelection = 'current'): Promise<CustomerBillingSnapshot> {
  return (await api.get<ApiResponse<CustomerBillingSnapshot>>(currentCustomerBillingKey, { params: { account: selection } })).data.data;
}
export async function openCustomerBillingPortal(selection: BillingAccountSelection = 'current'): Promise<{ url: string }> {
  return (await api.post<ApiResponse<{ url: string }>>('/billing/current/portal', {}, { params: { account: selection } })).data.data;
}

export async function startUsageBillingCheckout(): Promise<{ url: string }> {
  const response = await api.post<ApiResponse<{ url: string }>>('/billing/usage/current/checkout', {});
  return response.data.data;
}

export async function syncUsageBilling(checkoutSessionId: string): Promise<void> {
  await api.post('/billing/usage/current/sync', { checkoutSessionId });
}

export async function openUsageBillingPortal(): Promise<{ url: string }> {
  const response = await api.post<ApiResponse<{ url: string }>>('/billing/usage/current/portal');
  return response.data.data;
}

export async function refreshFixedBilling(selection: BillingAccountSelection = 'current'): Promise<void> {
  await api.post('/billing/current/fixed/refresh', {}, { params: { account: selection } });
}

export async function deactivateCurrentUsageBilling(): Promise<void> {
  await api.post('/billing/usage/current/deactivate', {});
}

export async function removeCurrentUsagePaymentMethods(): Promise<void> {
  await api.delete('/billing/usage/current/payment-methods');
}

export type CustomerInvoice = {
  id: string; number: string | null; status: string | null; currency: string;
  amountDueCents: number; amountRemainingCents: number;
  createdAt: string; dueAt: string | null; hostedInvoiceUrl: string | null;
};
export const currentInvoicesKey = '/billing/current/invoices';
export async function fetchCurrentInvoices(selection: BillingAccountSelection = 'current'): Promise<{ invoices: CustomerInvoice[]; hasMore: boolean }> {
  const response = await api.get<ApiResponse<{ invoices: CustomerInvoice[]; hasMore: boolean }>>(currentInvoicesKey, { params: { account: selection } });
  return response.data.data;
}

export interface CurrentFixedCardConsent { consentId?: string; agreementId: string; termsHash: string; monthlyAmountCents: number; text: string; state: 'not_authorized' | 'pending' | 'enabling' | 'active' | 'disabled'; collectionMethod: string; authorizedAt: string | null }
export async function fetchCurrentFixedCardConsent(): Promise<CurrentFixedCardConsent | null> { return (await api.get('/billing/current/fixed/card-consent')).data.data; }
export async function authorizeCurrentFixedCard(value: CurrentFixedCardConsent): Promise<{ state: string; url: string | null }> {
  return (await api.post('/billing/current/fixed/card-consent', { agreementId: value.agreementId, termsHash: value.termsHash, confirmed: true })).data.data;
}

export async function disableCurrentFixedCard(value: CurrentFixedCardConsent): Promise<{ state: 'disabled' }> {
  return (await api.post('/billing/current/fixed/card-consent/disable', { agreementId: value.agreementId, termsHash: value.termsHash, consentId: value.consentId, confirmed: true })).data.data;
}
